import update from "immutability-helper";
import _, { cloneDeep } from "lodash";
import { useEffect, useMemo, useState, useRef, useCallback } from "react";
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SettingsIcon from '@mui/icons-material/Settings';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
// import ShareIcon from '@mui/icons-material/Share';
import SortableList from "../../../../shared/dnd/SortableList";
import DragSVG from "../../../../../assets/svgs/drag.svg";
import api from "../../../../../services/api/api";
import { getAllQuestions } from "../../../../../services/api/question.api";
import { isFullAccess, isOnlyHospitalTabAccess, selectedTabAccess, mapQuestionToValues } from "../../../../../utilis/common";
import AlertDialog from "../../../../shared/AlertDialog";
import Button from "../../../../ui/button/Button";
import styles from "./QuestionSetup.module.scss";
import { updateQuestionLabel } from "../../../../../services/api/data-input.api";
import AddQuestionDialog from "./AddQuestion";
import { Dialog, DialogContent, DialogTitle, Divider, IconButton, FormControl, InputLabel, Select, MenuItem, Menu, ListItemIcon, ListItemText, Chip, Box } from "@mui/material";
import { styled } from "@mui/system";
import CloseIcon from '@mui/icons-material/Close';
import { useSelector } from "react-redux";
import { AUTH_ROLES } from "../../../../../types/auth.type";
import useResetDynamicCards from "../../../../hooks/useResetDynamicCards";
import { PAGE_TYPE } from "../../../../../types/pages.type";
import { QUESTION_INPUT_TYPE, QUESTIONS_TEMPLATE_TYPE, TIME_RANGE_TYPE } from "../../../../../types/question.type";
import QuestionsPreview from "./QuestionsPreview";
import AutoFillSettingsDialog from "./AutoFillSettingsDialog";
import AiConfigDialog from "../../../../shared/ai-config-dialog/AiConfigDialog";
import AIIcon from "../../../../../assets/svgs/AI-Icon.svg";
import ShareCustomQuestionDialog from "../../../../shared/add-filter-card/ShareCustomQuestionDialog";
import { ADT_SUB_TYPES } from "../../../../../types/common.type";
import useFacilityId from "../../../../../hooks/useFacilityId";
import useAccountId from "../../../../../hooks/useAccountId";
import ArchivedQuestionsList from "./ArchivedQuestionsList";
import { shareCustomQuestion } from "../../../../../services/custom-tab-sharing.service";
import { useDispatch } from "react-redux";
import { clearCachedResults as clearCachedResultsHospital } from "../../../../../store/reducers/hospital.slice";
import { clearCachedResults as clearCachedResultsAdmission } from "../../../../../store/reducers/admission.slice";
import { clearCachedResults as clearCachedResultsCommunity } from "../../../../../store/reducers/community-transfer.slice";
import { clearCachedResults as clearCachedResultsDeceased } from "../../../../../store/reducers/deceased.slice";
import { clearCachedResults as clearCachedResultsOverall } from "../../../../../store/reducers/overall.slice";

const initialValues = {
	label: "",
	tableLabel: "",
	isCustom: false,
	customQuestionInputType: "",
	customQuestionOptions: [],
	numberRangeType: null,
	isArchived: false,
	timeRangeType: TIME_RANGE_TYPE.HOURLY,
	templateType: QUESTIONS_TEMPLATE_TYPE.DX_CARD,
	autoFillEnabled: false,
	sourceTransitionType: '',
	sourceQuestionId: '',
	dateAnalysisStartQuestion: '',
	dateAnalysisEndQuestion: '',
	useCustomCardInDashboard: false,
	description: '',
	aiDescription: '',
	existingAccessor: '',
	shareUserIds: [],
	invisibleAnswerOptions: false,
	dataInputLabel: '',
	isUniversal: false,
}

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
	display: 'flex',
	justifyContent: 'space-between',
	alignItems: 'center',
	padding: theme.spacing(2, 3),
}));

// Custom hook for auto-scroll functionality during drag and drop
const useAutoScroll = (containerRef) => {
	const scrollIntervalRef = useRef(null);
	const scrollSpeed = 8;
	const scrollThreshold = 80;

	const startScroll = useCallback((direction) => {
		if (scrollIntervalRef.current) return;

		scrollIntervalRef.current = setInterval(() => {
			if (containerRef.current) {
				const currentScrollTop = containerRef.current.scrollTop;
				const maxScrollTop = containerRef.current.scrollHeight - containerRef.current.clientHeight;

				let newScrollTop;
				if (direction === 'up') {
					newScrollTop = Math.max(0, currentScrollTop - scrollSpeed);
				} else {
					newScrollTop = Math.min(maxScrollTop, currentScrollTop + scrollSpeed);
				}

				containerRef.current.scrollTop = newScrollTop;

				// Stop scrolling if we've reached the boundary
				if ((direction === 'up' && newScrollTop === 0) ||
					(direction === 'down' && newScrollTop === maxScrollTop)) {
					stopScroll();
				}
			}
		}, 16); // ~60fps
	}, [containerRef]); // eslint-disable-line react-hooks/exhaustive-deps

	const stopScroll = useCallback(() => {
		if (scrollIntervalRef.current) {
			clearInterval(scrollIntervalRef.current);
			scrollIntervalRef.current = null;
		}
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	const handleDragUpdate = useCallback((update) => {
		if (!containerRef.current || !update.draggableId) return;

		const container = containerRef.current;
		const containerRect = container.getBoundingClientRect();
		const mouseY = update.clientY || 0;

		// Check if mouse is near top or bottom edges
		const distanceFromTop = mouseY - containerRect.top;
		const distanceFromBottom = containerRect.bottom - mouseY;

		// Only scroll if we're not at the boundaries
		if (distanceFromTop < scrollThreshold && container.scrollTop > 0) {
			startScroll('up');
		} else if (distanceFromBottom < scrollThreshold &&
			container.scrollTop < container.scrollHeight - container.clientHeight) {
			startScroll('down');
		} else {
			stopScroll();
		}
	}, [containerRef, startScroll, stopScroll, scrollThreshold]);

	return { handleDragUpdate, stopScroll };
};

const QuestionSetup = ({ close, defaultType, defaultTransferType }) => {
	const { permission, activeFacilities, auth } = useSelector(
		({ auth, account, facility, permission, activeFacilities }) => ({
			auth,
			account,
			facility,
			permission,
			activeFacilities: activeFacilities.facilities
		})
	);


	const isOnlyHospitalDashboard = useMemo(() => {
		return isOnlyHospitalTabAccess();
	}, []);


	const transferOptions = useMemo(() => {
		const selectedTab = selectedTabAccess();
		return [
			...(_.includes(selectedTab, PAGE_TYPE.HOSPITAL) ? [{ label: "Hospital Transfer", value: "hospitalTransfer" }] : []),
			...(_.includes(selectedTab, PAGE_TYPE.COMMUNITY_TRANSFER) ? [
				{ label: "Total Community", value: "totalCommunity" },
				{ label: "Safe Discharge", value: "safeDischarge" },
				{ label: "SNF", value: "SNF" },
				{ label: "AMA", value: "AMA" }
			] : []),
			...(_.includes(selectedTab, PAGE_TYPE.DECEASED) ? [{ label: "Deceased", value: "deceased" }] : []),
		];
	}, []);

	const [list, setList] = useState(undefined);
	const [fullList, setFullList] = useState(undefined);
	const [defaultListQuestions, setDefaultListQuestions] = useState(undefined);
	const [dragDisabled, setDragDisabled] = useState(false);
	const [showAddQ, setShowAddQ] = useState(false);
	const [showArchiveList, setShowArchiveList] = useState(false);
	const [confirmDialog, setConfirmDialog] = useState(false);
	const [type, setType] = useState(defaultType !== undefined ? defaultType : "admission");
	const [transferType, setTransferType] = useState(defaultTransferType !== undefined ? defaultTransferType : "hospitalTransfer");
	const [lastSelectedTransferType, setLastSelectedTransferType] = useState(defaultTransferType !== undefined ? defaultTransferType : "hospitalTransfer");
	const [editQuestion, setEditQuestion] = useState(undefined);
	const [deleteIdx, setDeleteIdx] = useState(undefined);
	const [loading, setLoading] = useState(false);
	const [questionValues, setQuestionValues] = useState(initialValues);
	const [showAutoFillDialog, setShowAutoFillDialog] = useState(false);
	const [selectedQuestionForAutoFill, setSelectedQuestionForAutoFill] = useState(null);
	const [menuAnchorEl, setMenuAnchorEl] = useState(null);
	const [selectedQuestionIndex, setSelectedQuestionIndex] = useState(null);
	const resetDynamicCards = useResetDynamicCards();
	const [showDateAnalysisOptionsDialog, setShowDateAnalysisOptionsDialog] = useState(false);
	const [dateAnalysisQuestions, setDateAnalysisQuestions] = useState([]);
	const [selectedQuestionForDateAnalysis, setSelectedQuestionForDateAnalysis] = useState(null);
	const [showDateAnalysisDeleteDialog, setShowDateAnalysisDeleteDialog] = useState(false);
	const [showAiConfigDialog, setShowAiConfigDialog] = useState(false);
	const [selectedQuestionForAi, setSelectedQuestionForAi] = useState(null);
	const [questionAiStatus, setQuestionAiStatus] = useState({});
	const [showShareDialog, setShowShareDialog] = useState(false);
	const [selectedQuestionForShare, setSelectedQuestionForShare] = useState(null);
	const { facilityId } = useFacilityId();
	const { accountId: accountIdFromHook } = useAccountId();
	const [timeFieldQuestions, setTimeFieldQuestions] = useState([]);
	const dispatch = useDispatch();

	const resetQuestionValues = () => {
		setQuestionValues(initialValues)
	}

	useEffect(() => {
		if (isOnlyHospitalDashboard) {
			setType("transfer");
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Auto-scroll functionality for drag and drop
	const questionListRef = useRef(null);
	const { handleDragUpdate, stopScroll } = useAutoScroll(questionListRef);

	// Cleanup auto-scroll on unmount
	useEffect(() => {
		return () => {
			stopScroll();
		};
	}, [stopScroll]);

	const handleReorder = async (updatedList) => {
		if (!updatedList || !updatedList.length) return;

		handleDragUpdate({});
		const cloned = cloneDeep(updatedList);

		let modifiedList = await cloned.map((item, index) => {
			item.order.order = index;
			return item.order;
		});

		setDragDisabled(true);
		await api.put(`/api/question/set/?id=${cloned[0]._id}`, modifiedList);
		setDragDisabled(false);
		setList(cloned);
		setFullList(cloned);
	};

	const getQuestions = async () => {
		const latestType = isOnlyHospitalDashboard && type !== "return" ? "transfer" : type;
		const questions = await getAllQuestions({
			facilityid: facilityId,
			forType: latestType,
			listType: "question",
			...(latestType === "transfer" && { forTransferType: transferType })
		});
		const timeFieldQuestions = questions?.filter(ele => ele?.question?.customQuestionInputType === QUESTION_INPUT_TYPE.TIME_TAB_RESIDENT_LIST || (!ele?.question?.customQuestionInputType && ele?.question?.type === "time") || (ele?.question?.customQuestionInputType === QUESTION_INPUT_TYPE.TIME_TAB_RANGE && !ele?.question?.existingAccessor));
		const timeFieldQuestionsArray = timeFieldQuestions?.filter(ele => ele?.question?.accessor !== "shiftData");
		setTimeFieldQuestions(timeFieldQuestionsArray ?? []);
		setFullList(questions ?? []);
		const updatedQuestions = questions?.length > 0 ? questions?.filter(ele =>
			ele?.question?.customQuestionInputType !== QUESTION_INPUT_TYPE.DATE_ANALYSIS &&
			!(ele?.question?.customQuestionInputType === QUESTION_INPUT_TYPE.TIME_TAB_RANGE && ele?.question?.existingAccessor) &&
			!ele?.question?.isDefaultCard
		)?.filter(Boolean) : [];
		setList(updatedQuestions);
		setDefaultListQuestions(questions ?? []);

		// Load AI status for all questions
		loadQuestionAiStatus(updatedQuestions);
	};

	const loadQuestionAiStatus = useCallback(async (questions) => {
		if (!questions || questions.length === 0 || !activeFacilities || activeFacilities.length === 0) return;

		try {
			const questionIds = questions.map(q => q.question._id);
			const facilityIds = activeFacilities; // Use active facilities from Redux

			const response = await api.post('/api/ai-autofill/ai-status-batch', {
				questionIds,
				facilityIds
			});

			if (response?.success) {
				const aiStatusMap = {};
				response.data.forEach(item => {
					aiStatusMap[item.questionId] = item.aiEnabled;
				});
				setQuestionAiStatus(aiStatusMap);
			}
		} catch (error) {
			console.error('Error loading AI status:', error);
			// Don't show error to user, just continue without AI icons
		}
	}, [activeFacilities]);

	const clearCachedResults = () => {
		dispatch(clearCachedResultsHospital());
		dispatch(clearCachedResultsAdmission());
		dispatch(clearCachedResultsCommunity());
		dispatch(clearCachedResultsDeceased());
		dispatch(clearCachedResultsOverall());
	}
	const saveQuestion = async (isCustomQuestion, updatedValues = null, extraParams = {}) => {
		const questionValuesData = updatedValues ? updatedValues : questionValues;
		let newQuestion;
		setLoading(true);
		const shareIfNeeded = async (questionId) => {
			if (!isCustomQuestion) return;
			const recipients = Array.isArray(questionValuesData?.shareUserIds) ? questionValuesData.shareUserIds.filter(Boolean) : [];
			if (!questionId) return;
			try {
				// Allow empty recipient list to clear shares
				await shareCustomQuestion(questionId, recipients, '', questionValuesData?.isUniversal || false);
			} catch (err) {
				console.error("Failed to share custom question", err);
			}
		};
		if (editQuestion?._id) {

			const latestType = isOnlyHospitalDashboard && type !== "return" ? "transfer" : type;

			let updatedQ = await updateQuestionLabel(
				{
					label: questionValuesData.label,
					tableLabel: questionValuesData.tableLabel,
					isArchived: questionValuesData?.isArchived ?? false,
					templateType: questionValuesData?.templateType,
					timeRangeType: questionValuesData?.timeRangeType,
					description: questionValuesData?.description,
					aiDescription: questionValuesData?.aiDescription,
					forType: latestType,
					...(isCustomQuestion && { customData: questionValuesData }),
					isUniversal: questionValuesData?.isUniversal || false,
					...(latestType === "transfer" && { forTransferType: transferType }),
					...(editQuestion?.forTransferType && editQuestion?.forTransferType === ADT_SUB_TYPES.TOTAL_COMMUNITY && { mainTransferType: editQuestion?.forTransferType }),
					...extraParams,
				},
				editQuestion._id
			);

			let indexAAA = list.findIndex(item => item.question._id === editQuestion._id);
			const questions = updatedQ?.data ?? updatedQ;
			if (questions && indexAAA !== -1) {
				update(list, {
					[indexAAA]: {
						question: {
							$set: questions,
						},
					},
				});
				update(fullList, {
					[indexAAA]: {
						question: {
							$set: questions,
						},
					},
				});
			}
			await shareIfNeeded(editQuestion?._id);
			resetDynamicCards();
			getQuestions();
			setLoading(false);
			setEditQuestion(undefined);
			resetQuestionValues()
			setShowAddQ(false);
			clearCachedResults();
		} else {
			newQuestion = await api.post("/api/question/set", {
				facilityId: facilityId,
				label: questionValuesData.label,
				tableLabel: questionValuesData?.tableLabel,
				isArchived: questionValuesData?.isArchived ?? false,
				forType: type,
				forTransferType: transferType,
				active: true,
				type: "text",
				accessor: questionValuesData?.label?.replace(/\s/g, ""),
				allowAfter: true,
				isEditable: true,
				accountId: accountIdFromHook,
				customData: questionValuesData,
				templateType: questionValuesData?.templateType,
			});

			let modifiedList = await fullList.map((item, index) => {
				item.order.order = index;
				return item.order;
			});

			if (transferType === ADT_SUB_TYPES.TOTAL_COMMUNITY) {
				await api.post("/api/question/create-total-community-question-order", {
					newQuestionId: newQuestion._id
				});
			} else {
				await api.put(`/api/question/set/?id=${list[0]._id}`, [
					...modifiedList,
					{ order: list.length, questionId: newQuestion._id },
				]);

			}
			await shareIfNeeded(newQuestion?._id);
			resetDynamicCards();
			getQuestions();
			setLoading(false);
			resetQuestionValues()
			setShowAddQ(false);
			clearCachedResults();
		}
	};

	useEffect(() => {
		getQuestions();
		// eslint-disable-next-line
	}, []);


	useEffect(() => {
		getQuestions();
		// eslint-disable-next-line
	}, [type, transferType]);

	// Reload AI status when facility changes
	useEffect(() => {
		if (list && list.length > 0 && activeFacilities && activeFacilities.length > 0) {
			loadQuestionAiStatus(list);
		}
	}, [activeFacilities, list, loadQuestionAiStatus]);

	const deleteQuestion = async (id, idx, isArchived = false) => {
		await api.delete(`/api/question/set/?id=${id}${isArchived ? '&archived=true' : ''}`);

		// remove deleted question from list
		update(list, {
			$splice: [[idx, 1]],
		});

		// let updatedFullList = update(fullList, {
		// 	$splice: [[idx, 1]],
		// });

		// let modifiedList = await updatedFullList.map((item, index) => {
		// 	item.order.order = index;
		// 	return item.order;
		// });

		// due to remove last item from question order const newOrder = await api.put(`/api/question/set/?id=${list[0]._id}`, modifiedList);
		resetDynamicCards();
		getQuestions();
	};

	const handleClose = () => {
		close();
		setEditQuestion(undefined);
	};

	const handleCustomQuestion = () => {
		const isSuperOrAdmin = [AUTH_ROLES.SUPER, AUTH_ROLES.ADMIN].includes(permission?.role);
		if (!isSuperOrAdmin) return false;

		return editQuestion ? !!editQuestion.isCustom : true;
	};

	const [isArchivedDialog, setIsArchivedDialog] = useState(false);
	const [questionPreview, setQuestionPreview] = useState(null);

	const handleViewQuestion = async (question) => {
		setQuestionPreview(question);
	}

	const handleSaveAutoFillSettings = async (autoFillData) => {
		if (!selectedQuestionForAutoFill) return;

		try {
			setLoading(true);
			getQuestions();
			setLoading(false);
			setSelectedQuestionForAutoFill(null);
		} catch (error) {
			console.error('Error refreshing questions after auto-fill save:', error);
			setLoading(false);
		}
	}

	// Menu handlers
	const handleMenuClick = (event, questionIndex) => {
		setMenuAnchorEl(event.currentTarget);
		setSelectedQuestionIndex(questionIndex);
	};

	const handleMenuClose = () => {
		setMenuAnchorEl(null);
		setSelectedQuestionIndex(null);
	};


	// eslint-disable-next-line no-unused-vars
	const handleShareQuestion = (question) => {
		handleMenuClose();
		setSelectedQuestionForShare(question);
		setShowShareDialog(true);
	};

	const handleShareDialogClose = () => {
		setShowShareDialog(false);
		setSelectedQuestionForShare(null);
	};

	const handleShareSuccess = () => {
		// Optional callback for when share is successful
		// You can add any additional logic here if needed
	};

	const handleEditQuestion = (question) => {
		if (question?.usedAsDependentQuestion) {
			// Determine question type (date or time)
			const isDateQuestion = question.type === "date";
			const isTimeQuestion =
				// Legacy / special-case accessor
				(question.accessor === "transferTime") ||
				// New time-tab-range questions with a valid existingAccessor
				(
					question.type === "time" &&
					question.customQuestionInputType === QUESTION_INPUT_TYPE.TIME_TAB_RANGE
				);
			// Find all questions that use this question in date/time analysis
			const relatedQuestions = defaultListQuestions?.filter(ele => {
				if (isDateQuestion && ele?.question?.customQuestionInputType === QUESTION_INPUT_TYPE.DATE_ANALYSIS) {
					return ele?.question?.dateAnalysisStartQuestion === question.accessor ||
						ele?.question?.dateAnalysisEndQuestion === question.accessor || (ele?.question?.isDefaultCard && ele?.question?.customQuestionInputType === QUESTION_INPUT_TYPE.DATE_ANALYSIS);
				}
				if (isTimeQuestion && ele?.question?.customQuestionInputType === QUESTION_INPUT_TYPE.TIME_TAB_RANGE) {
					// For time questions, check if they're used in time range questions
					if (question.accessor === "transferTime") {
						return ele?.question?.existingAccessor === question.accessor || (ele?.question?.isDefaultCard && ele?.question?.customQuestionInputType === QUESTION_INPUT_TYPE.TIME_TAB_RANGE);
					} else {
						// You may need to add timeAnalysisStartQuestion and timeAnalysisEndQuestion fields if they exist
						return ele?.question?.existingAccessor === question.accessor || (ele?.question?.accessor !== "shiftData" && ele?.question?.isDefaultCard && ele?.question?.customQuestionInputType === QUESTION_INPUT_TYPE.TIME_TAB_RANGE);
					}
				}
				return false;
			}) || [];

			setDateAnalysisQuestions(relatedQuestions);
			setSelectedQuestionForDateAnalysis(question);
			setShowDateAnalysisOptionsDialog(true);
			handleMenuClose();
		} else {
			setQuestionValues(mapQuestionToValues(question));
			setEditQuestion(question);
			setShowAddQ(true);
			handleMenuClose();
		}
	};

	const handleAutoFillSettings = (question) => {
		setSelectedQuestionForAutoFill(question);
		setShowAutoFillDialog(true);
		handleMenuClose();
	};

	const handleAiConfig = (questionOrder) => {
		setSelectedQuestionForAi({ ...questionOrder?.question, order: questionOrder?.order });
		setShowAiConfigDialog(true);
		handleMenuClose();
	};

	// Helper function to check if question type supports auto-fill
	const supportsAutoFill = (question) => {
		// Date analysis questions support auto-fill
		if (question?.customQuestionInputType === QUESTION_INPUT_TYPE.DATE_ANALYSIS) {
			return true;
		}

		if (question?.isCustom) {
			// No auto-fill for questions with fixed options (dropdown, yes/no, etc.)
			const fixedOptionTypes = [
				QUESTION_INPUT_TYPE.LIMITED_ANSWERS,
				QUESTION_INPUT_TYPE.NUMBER_RANGE_LIMITED_ANSWERS,
				QUESTION_INPUT_TYPE.TIME_TAB_RESIDENT_LIST,
			];
			return !fixedOptionTypes.includes(question?.customQuestionInputType);
		} else {
			if (question?.accessor === "dateOfLatestAdmission" ||
				question?.accessor === "type" ||
				question?.accessor === "transferType"
			) {
				return false;
			}
			return true
		}
	};

	// Helper function to check if we should show delete option for date analysis questions
	const shouldShowDeleteForDateAnalysis = (question) => {
		return question?.customQuestionInputType === QUESTION_INPUT_TYPE.DATE_ANALYSIS && question?.isEditable;
	};

	// Helper function to check if question type supports AI
	const supportsAI = (question) => {

		// AI can be enabled for most field types
		const supportedTypes = ['text', 'validation', 'date', 'time'];

		if (supportedTypes.includes(question?.type)) {
			return true;
		}


		// AI can help with custom questions that accept various input types
		if (question?.isCustom) {
			const aiSupportedTypes = [
				QUESTION_INPUT_TYPE.UNLIMITED_ANSWERS,
				QUESTION_INPUT_TYPE.NUMBER_RANGE,
				QUESTION_INPUT_TYPE.LIMITED_ANSWERS,
				QUESTION_INPUT_TYPE.NUMBER_RANGE_LIMITED_ANSWERS,
				QUESTION_INPUT_TYPE.DATE_ANALYSIS,
				QUESTION_INPUT_TYPE.TIME_TAB_RANGE,
				// Add more types that can benefit from AI assistance
			];
			return aiSupportedTypes.includes(question?.customQuestionInputType);
		}

		// AI can also help with toggle fields for better context understanding
		if (question?.type === 'toggle') {
			return true;
		}
		if (question?.isArchived) {
			return true;
		}

		return false;
	};

	return (
		<>
			{!!questionPreview && (
				<QuestionsPreview
					handleClose={() => {
						setQuestionPreview(null);
					}}
					onClose={() => setQuestionPreview(null)}
					question={questionPreview}
					isPreview={true}
				/>
			)}
			{!!showAutoFillDialog && selectedQuestionForAutoFill && (
				<AutoFillSettingsDialog
					open={showAutoFillDialog}
					onClose={() => {
						setShowAutoFillDialog(false);
						setSelectedQuestionForAutoFill(null);
					}}
					question={selectedQuestionForAutoFill}
					onSave={handleSaveAutoFillSettings}
					latestType={isOnlyHospitalDashboard && type !== "return" ? "transfer" : type}
					transferType={transferType}
				/>
			)}
			{!!showAiConfigDialog && selectedQuestionForAi && (
				<AiConfigDialog
					open={showAiConfigDialog}
					onClose={() => {
						setShowAiConfigDialog(false);
						setSelectedQuestionForAi(null);
					}}
					question={selectedQuestionForAi}
					onSave={(aiSettings) => {
						// Refresh the questions list to show updated AI settings
						getQuestions();
					}}
					facilityId={facilityId}
				/>
			)}
			{!!isArchivedDialog && (
				<AlertDialog
					{...(editQuestion?._id && { isArchived: questionValues?.isArchived ?? false })}
					content="Archive question?"
					handleClose={async () => {
						const updatedValues = {
							...questionValues,
							isArchived: false,
						};
						await setQuestionValues(updatedValues);
						setIsArchivedDialog(false);
						saveQuestion(handleCustomQuestion(), updatedValues);
					}}
					withYesNo={true}
					handleSubmit={async () => {
						const updatedValues = {
							...questionValues,
							isArchived: true,
						};
						await setQuestionValues(updatedValues);
						setIsArchivedDialog(false);
						setConfirmDialog(false);
						saveQuestion(handleCustomQuestion(), updatedValues);
					}}
				/>
			)}
			{!!confirmDialog && (
				<AlertDialog
					content="New Question will be display for all account facility."
					handleClose={() => setConfirmDialog(false)}
					handleSubmit={async () => {
						setConfirmDialog(false);
						if (questionValues?.customQuestionInputType === QUESTION_INPUT_TYPE.DATE_ANALYSIS) {
							const updatedValues = {
								...questionValues,
								isArchived: false,
							};
							await setQuestionValues(updatedValues);
							setIsArchivedDialog(false);
							setConfirmDialog(false);
							saveQuestion(handleCustomQuestion(), updatedValues);
						} else {
							setIsArchivedDialog(true);
						}
					}}
				/>
			)}
			<Dialog open={true} maxWidth="xl" fullWidth={true}>
				<StyledDialogTitle>
					{editQuestion?._id ? 'Edit Question' : 'Add Question'}
					<IconButton aria-label="close" onClick={handleClose}>
						<CloseIcon />
					</IconButton>
				</StyledDialogTitle>
				<Divider />
				<DialogContent>
					{showAddQ && (
						<AddQuestionDialog
							open={showAddQ}
							setIsArchivedDialog={setIsArchivedDialog}
							onClose={() => setShowAddQ(false)}
							onSave={saveQuestion}
							refreshQuestions={getQuestions}
							editQuestion={editQuestion}
							loading={loading}
							setEditQuestion={setEditQuestion}
							setQuestionValues={setQuestionValues}
							questionValues={questionValues}
							setConfirmDialog={setConfirmDialog}
							resetQuestionValues={resetQuestionValues}
							isCustomQuestion={handleCustomQuestion()}
							latestType={type}
							transferType={transferType}
							currentQuestions={list}
							timeFieldQuestions={timeFieldQuestions}
						/>
					)}
					{showArchiveList && (
						<ArchivedQuestionsList
							onClose={() => setShowArchiveList(false)}
							refreshParent={getQuestions}
							defaultType={defaultType}
							defaultTransferType={defaultTransferType}
						/>
					)}
					<div className={styles.questionSetup}>
						<div className={styles.content}>
							<div className={`df aic ffmar fs15 fw500 p-r-20 p-l-20 ${styles.tabsWrpr}`}>
								{_.includes(selectedTabAccess(), PAGE_TYPE.ADMISSION) && (
									<>
										<div
											className={`df acc ${styles.tab} ${type === "admission" ? styles.active : ""}`}
											onClick={() => {
												setType("admission");
												setLastSelectedTransferType(transferType ?? "hospitalTransfer");
												setTransferType(null);
											}}
										>
											Admissions
										</div>
									</>
								)}
								{isFullAccess() && (
									<div
										className={`df acc ${styles.tab} ${type === "return" ? styles.active : ""}`}
										onClick={() => {
											setType("return");
											setLastSelectedTransferType(transferType ?? "hospitalTransfer");
											setTransferType(null);
										}}
									>
										Returns
									</div>
								)}
								{[PAGE_TYPE.HOSPITAL, PAGE_TYPE.COMMUNITY_TRANSFER, PAGE_TYPE.DECEASED].some(ele => selectedTabAccess().includes(ele)) && (
									<div
										className={`df acc ${styles.tab} ${type === "transfer" ? styles.active : ""}`}
										onClick={() => {
											setType("transfer")
											setTransferType(lastSelectedTransferType ?? "hospitalTransfer");
										}}
									>
										Transfers
									</div>
								)}
								{type === "transfer" ? (
									<FormControl sx={{ minWidth: 275, ml: 2 }}>
										<InputLabel>Transfer Type</InputLabel>
										<Select
											value={transferType}
											onChange={(e) => setTransferType(e.target.value)}
											label="Transfer Type"
											size="small"
										>
											{transferOptions.map((option) => (
												<MenuItem key={option.value} value={option.value}>
													{option.label}
												</MenuItem>
											))}
										</Select>
									</FormControl>
								) : undefined}

								<div className={`mla df aic`}>
									<Button text="Add Question" buttonStyle="theme" size="medium" action={() => {
										setLoading(false);
										resetQuestionValues();
										setShowAddQ(!showAddQ)
									}} />
									{([AUTH_ROLES.SUPER].includes(permission?.role) || auth?.type === "superAdmin") && (
										<Box sx={{ ml: 1 }}>
											<Button
												text="Manage Archive questions"
												buttonStyle="theme"
												size="medium"
												action={() => setShowArchiveList(true)}
											/>
										</Box>
									)}
								</div>
							</div>
							<p
								style={{
									padding: "14px 22px 10px",
									fontStyle: "italic",
								}}
							>
								Click and drag questions to adjust the question order
							</p>
							{list && (
								<div className={styles.questionList} ref={questionListRef}>
									<SortableList
										items={list}
										getId={(_, index) => `id-${index}`}
										onReorder={handleReorder}
										disabled={dragDisabled}
									>
										{(Item, ids) => (
											<div>
												{list?.map((itm, idx) => (
													<Item key={ids[idx]} id={ids[idx]} disabled={dragDisabled}>
														<p
															className={`ffmsb df aic ${styles.line}`}
														>
															<span className={styles.iconWrpr}>
																<DragSVG />
															</span>
															<span className={styles.num}>{idx + 1}.</span>
															<span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
																{itm.order?.label || itm.question.label}
																{questionAiStatus[itm.question._id] && (
																	<AIIcon
																		sx={{
																			fontSize: '16px',
																			color: '#4caf50',
																			opacity: 1
																		}}
																		title="AI Enabled"
																	/>
																)}
																{itm.question?.isShared && (
																	<Chip
																		label={itm.question?.isHidden ? "hidden" : "shared"}
																		size="small"
																		variant="outlined"
																		color={itm.question?.isHidden ? "warning" : "success"}
																		sx={{
																			height: '14px',
																			fontSize: '9px',
																			fontWeight: 'bold',
																			'& .MuiChip-label': {
																				padding: '0 3px',
																				marginTop: "1.5px",
																				fontSize: '9px',
																				fontWeight: 'bold',
																				textTransform: 'lowercase'
																			}
																		}}
																	/>
																)}
																{itm.question?.isArchived && (
																	<Chip
																		label={itm.question?.questionRefId ? "Archived Ref" : "Archived"}
																		size="small"
																		variant="outlined"
																		color="info"
																		sx={{
																			height: '14px',
																			fontSize: '9px',
																			fontWeight: 'bold',
																			'& .MuiChip-label': {
																				padding: '0 3px',
																				marginTop: "1.5px",
																				fontSize: '9px',
																				fontWeight: 'bold',
																				textTransform: 'capitalize'
																			}
																		}}
																	/>
																)}
															</span>
															<div className={`df aic mla`}>
																<IconButton
																	className={`m-r-15 ${styles.action}`}
																	onClick={(event) => handleMenuClick(event, idx)}
																	size="small"
																	sx={{
																		padding: '8px',
																		borderRadius: '6px',
																		backgroundColor: 'rgba(0, 0, 0, 0.04)',
																		'&:hover': {
																			backgroundColor: 'rgba(0, 0, 0, 0.08)',
																		},
																		transition: 'all 0.2s ease-in-out'
																	}}
																>
																	<MoreVertIcon sx={{ fontSize: '18px', color: '#666' }} />
																</IconButton>
																{deleteIdx === idx && (
																	<div className={`df aic ${styles.confirmDelete}`}>
																		<button
																			className={`m-r-10`}
																			onClick={() => {
																				setDeleteIdx(undefined);
																			}}
																		>
																			Cancel
																		</button>
																		<button
																			className={styles.confirmDeleteBtn}
																			onClick={() => {
																				deleteQuestion(itm.question._id, idx, itm?.question?.isArchived);
																				setDeleteIdx(undefined);
																			}}
																		>
																			Confirm
																		</button>
																	</div>
																)}
															</div>
														</p>
													</Item>
												))}
											</div>
										)}
									</SortableList>
								</div>
							)}
						</div>
					</div>
				</DialogContent>
			</Dialog>

			{/* Material-UI Menu for question actions */}
			<Menu
				anchorEl={menuAnchorEl}
				open={Boolean(menuAnchorEl)}
				onClose={handleMenuClose}
				PaperProps={{
					elevation: 3,
					sx: {
						minWidth: 180,
						borderRadius: 2,
						mt: 1,
						'& .MuiMenuItem-root': {
							px: 2,
							py: 0.5,
							borderRadius: 1,
							mx: 1,
							my: 0.25,
							fontSize: '13px',
							minHeight: '32px',
							'&:hover': {
								backgroundColor: 'rgba(0, 0, 0, 0.04)',
							},
						},
					},
				}}
			>
				{selectedQuestionIndex !== null && list[selectedQuestionIndex] && [
					...(!list[selectedQuestionIndex]?.question?.isArchived && !list[selectedQuestionIndex]?.question?.isShared ? [
						<MenuItem
							key="edit"
							dense
							onClick={() => handleEditQuestion(list[selectedQuestionIndex].question)}
						>
							<ListItemIcon sx={{ minWidth: 36 }}>
								<EditIcon fontSize="small" />
							</ListItemIcon>
							<ListItemText primary="Edit" />
						</MenuItem>,
						...(supportsAutoFill(list[selectedQuestionIndex].question) ? [
							<Divider key="divider1" sx={{ my: 0.25 }} />,
							<MenuItem
								key="autofill"
								dense
								onClick={() => handleAutoFillSettings(list[selectedQuestionIndex].question)}
								sx={{
									backgroundColor: list[selectedQuestionIndex]?.question?.autoFillEnabled ? 'rgba(25, 118, 210, 0.12)' : 'transparent',
									'&:hover': {
										backgroundColor: list[selectedQuestionIndex]?.question?.autoFillEnabled ? 'rgba(25, 118, 210, 0.2)' : 'rgba(0, 0, 0, 0.04)',
									},
								}}
							>
								<ListItemIcon sx={{ minWidth: 36 }}>
									<SettingsIcon
										fontSize="small"
										sx={{
											color: list[selectedQuestionIndex]?.question?.autoFillEnabled ? '#1976d2' : 'inherit',
										}}
									/>
								</ListItemIcon>
								<ListItemText
									primary="Auto Fill Settings"
									primaryTypographyProps={{
										color: list[selectedQuestionIndex]?.question?.autoFillEnabled ? '#1976d2' : 'inherit',
										fontWeight: list[selectedQuestionIndex]?.question?.autoFillEnabled ? 600 : 400,
									}}
								/>
							</MenuItem>
						] : []),
						...(supportsAI(list[selectedQuestionIndex].question) && [AUTH_ROLES.SUPER, AUTH_ROLES.TOTAL].includes(permission?.role) ? [
							<Divider key="divider2" sx={{ my: 0.25 }} />,
							<MenuItem
								key="ai-config"
								dense
								onClick={() => handleAiConfig(list[selectedQuestionIndex])}
							>
								<ListItemIcon sx={{ minWidth: 36 }}>
									<AIIcon
										fontSize="small"
										sx={{
											color: questionAiStatus[list[selectedQuestionIndex].question._id] ? '#4caf50' : '#1976d2'
										}}
									/>
								</ListItemIcon>
								<ListItemText
									primary={questionAiStatus[list[selectedQuestionIndex].question._id] ? "AI Enabled" : "Enable AI"}
								/>
							</MenuItem>
						] : []),
						// ...((list[selectedQuestionIndex].question?.customQuestionInputType) ? [
						// 	<Divider key="divider4" sx={{ my: 0.25 }} />,
						// 	<MenuItem
						// 		key="share"
						// 		dense
						// 		onClick={() => handleShareQuestion(list[selectedQuestionIndex].question)}
						// 	>
						// 		<ListItemIcon sx={{ minWidth: 36 }}>
						// 			<ShareIcon fontSize="small" />
						// 		</ListItemIcon>
						// 		<ListItemText primary="Share" />
						// 	</MenuItem>
						// ] : []),
					] : [
						<MenuItem
							key="view"
							dense
							onClick={() => handleViewQuestion(list[selectedQuestionIndex].question)}
						>
							<ListItemIcon sx={{ minWidth: 36 }}>
								<VisibilityOutlinedIcon fontSize="small" />
							</ListItemIcon>
							<ListItemText primary="View" />
						</MenuItem>,
						...(([AUTH_ROLES.SUPER].includes(permission?.role) || auth?.type === "superAdmin") ? [
							<Divider key="divider-edit-archive" sx={{ my: 0.25 }} />,
							<MenuItem
								key="editArchive"
								dense
								onClick={() => handleEditQuestion(list[selectedQuestionIndex].question)}
							>
								<ListItemIcon sx={{ minWidth: 36 }}>
									<EditIcon fontSize="small" />
								</ListItemIcon>
								<ListItemText primary="Edit Archive Question" />
							</MenuItem>] : []),
						...(supportsAutoFill(list[selectedQuestionIndex].question) ? [
							<Divider key="divider-archived-autofill" sx={{ my: 0.25 }} />,
							<MenuItem
								key="autofill-archived"
								dense
								onClick={() => handleAutoFillSettings(list[selectedQuestionIndex].question)}
								sx={{
									backgroundColor: list[selectedQuestionIndex]?.question?.autoFillEnabled ? 'rgba(25, 118, 210, 0.12)' : 'transparent',
									'&:hover': {
										backgroundColor: list[selectedQuestionIndex]?.question?.autoFillEnabled ? 'rgba(25, 118, 210, 0.2)' : 'rgba(0, 0, 0, 0.04)',
									},
								}}
							>
								<ListItemIcon sx={{ minWidth: 36 }}>
									<SettingsIcon
										fontSize="small"
										sx={{
											color: list[selectedQuestionIndex]?.question?.autoFillEnabled ? '#1976d2' : 'inherit',
										}}
									/>
								</ListItemIcon>
								<ListItemText
									primary="Auto Fill Settings"
									primaryTypographyProps={{
										color: list[selectedQuestionIndex]?.question?.autoFillEnabled ? '#1976d2' : 'inherit',
										fontWeight: list[selectedQuestionIndex]?.question?.autoFillEnabled ? 600 : 400,
									}}
								/>
							</MenuItem>
						] : []),
						...(supportsAI(list[selectedQuestionIndex].question) && [AUTH_ROLES.SUPER, AUTH_ROLES.TOTAL].includes(permission?.role) ? [
							<Divider key="divider-archived-ai" sx={{ my: 0.25 }} />,
							<MenuItem
								key="ai-config-archived"
								dense
								onClick={() => handleAiConfig(list[selectedQuestionIndex])}
							>
								<ListItemIcon sx={{ minWidth: 36 }}>
									<AIIcon
										fontSize="small"
										sx={{
											color: questionAiStatus[list[selectedQuestionIndex].question._id] ? '#4caf50' : '#1976d2'
										}}
									/>
								</ListItemIcon>
								<ListItemText
									primary={questionAiStatus[list[selectedQuestionIndex].question._id] ? "AI Enabled" : "Enable AI"}
								/>
							</MenuItem>
						] : [])
					]),
					...(!list[selectedQuestionIndex]?.question?.isShared && (list[selectedQuestionIndex].question.isEditable || shouldShowDeleteForDateAnalysis(list[selectedQuestionIndex].question)) ? [
						<Divider key="divider3" sx={{ my: 0.25 }} />,
						<MenuItem
							key="delete"
							dense
							onClick={() => {
								// For date analysis questions, use the special delete dialog
								if (shouldShowDeleteForDateAnalysis(list[selectedQuestionIndex].question)) {
									setSelectedQuestionForDateAnalysis(list[selectedQuestionIndex].question);
									setShowDateAnalysisDeleteDialog(true);
									handleMenuClose();
								} else {
									setDeleteIdx(selectedQuestionIndex);
									handleMenuClose();
								}
							}}
							sx={{ color: 'error.main' }}
						>
							<ListItemIcon sx={{ minWidth: 36, color: 'error.main' }}>
								<DeleteIcon fontSize="small" />
							</ListItemIcon>
							<ListItemText primary="Delete" />
						</MenuItem>
					] : [])
				]}
			</Menu>

			{/* Date Analysis Delete Confirmation Dialog */}
			{showDateAnalysisDeleteDialog && (() => {
				const isDateQuestion = selectedQuestionForDateAnalysis?.type === "date";
				const isTimeQuestion = selectedQuestionForDateAnalysis?.type === "time";
				const questionTypeLabel = isDateQuestion ? "date" : isTimeQuestion ? "time" : "date";

				return (
					<AlertDialog
						content={selectedQuestionForDateAnalysis?.customQuestionInputType === QUESTION_INPUT_TYPE.DATE_ANALYSIS
							? `Delete "${selectedQuestionForDateAnalysis?.order?.label || selectedQuestionForDateAnalysis?.label}"? This will permanently remove this ${questionTypeLabel} analysis question.`
							: `Delete "${selectedQuestionForDateAnalysis?.order?.label || selectedQuestionForDateAnalysis?.label}" and all related ${questionTypeLabel} analysis questions? This ${questionTypeLabel} question is used by ${dateAnalysisQuestions.length} ${questionTypeLabel} analysis question${dateAnalysisQuestions.length > 1 ? 's' : ''}, which will also be deleted.`
						}
						handleClose={() => setShowDateAnalysisDeleteDialog(false)}
						withYesNo={true}
						handleSubmit={async () => {
							try {
								if (selectedQuestionForDateAnalysis) {
									// Find the question in the list to get the index
									const questionIndex = list.findIndex(item =>
										item.question._id === selectedQuestionForDateAnalysis._id
									);

									if (questionIndex !== -1) {
										await deleteQuestion(
											selectedQuestionForDateAnalysis._id,
											questionIndex,
											selectedQuestionForDateAnalysis?.isArchived
										);
									}

									setShowDateAnalysisDeleteDialog(false);
									setShowDateAnalysisOptionsDialog(false);
									setSelectedQuestionForDateAnalysis(null);
								}
							} catch (error) {
								console.error('Error deleting date analysis question:', error);
							}
						}}
					/>
				);
			})()}

			{/* Date Analysis Options Dialog */}
			<Dialog
				open={showDateAnalysisOptionsDialog}
				onClose={() => {
					setShowDateAnalysisOptionsDialog(false);
					setSelectedQuestionForDateAnalysis(null);
				}}
				maxWidth="sm"
				fullWidth
			>
				<StyledDialogTitle>
					Question Options
					<div style={{ display: 'flex', gap: '8px' }}>
						{selectedQuestionForDateAnalysis?.customQuestionInputType === QUESTION_INPUT_TYPE.DATE_ANALYSIS && (
							<IconButton
								aria-label="delete"
								onClick={() => setShowDateAnalysisDeleteDialog(true)}
								sx={{
									color: 'error.main',
									'&:hover': {
										backgroundColor: 'rgba(211, 47, 47, 0.04)',
									}
								}}
							>
								<DeleteIcon />
							</IconButton>
						)}
						<IconButton aria-label="close" onClick={() => {
							setShowDateAnalysisOptionsDialog(false);
							setSelectedQuestionForDateAnalysis(null);
						}}>
							<CloseIcon />
						</IconButton>
					</div>
				</StyledDialogTitle>
				<Divider />
				<DialogContent sx={{ padding: '24px' }}>
					{(() => {
						const isDateQuestion = selectedQuestionForDateAnalysis?.type === "date";
						const isTimeQuestion = selectedQuestionForDateAnalysis?.type === "time";
						const questionTypeLabel = isDateQuestion ? "Date" : isTimeQuestion ? "Time" : "Date";

						return (
							<>
								{/* Current Question Section */}
								<div style={{ marginBottom: '24px' }}>
									<h4 style={{ margin: '0 0 12px 0', color: '#333', fontSize: '14px', fontWeight: '600' }}>
										{selectedQuestionForDateAnalysis ?
											(selectedQuestionForDateAnalysis.order?.label || selectedQuestionForDateAnalysis.label) : ''}
									</h4>
									<Button
										text="Edit Question"
										buttonStyle="theme"
										size="medium"
										action={() => {
											if (selectedQuestionForDateAnalysis) {
												setShowDateAnalysisOptionsDialog(false);
												setQuestionValues({
													label: selectedQuestionForDateAnalysis.order?.label || selectedQuestionForDateAnalysis.label,
													tableLabel: selectedQuestionForDateAnalysis.order?.tableLabel || selectedQuestionForDateAnalysis.tableLabel,
													customQuestionInputType: selectedQuestionForDateAnalysis?.customQuestionInputType,
													customQuestionOptions: selectedQuestionForDateAnalysis?.customQuestionOptions,
													templateType: selectedQuestionForDateAnalysis?.templateType ?? QUESTIONS_TEMPLATE_TYPE.DX_CARD,
													numberRangeType: selectedQuestionForDateAnalysis?.customQuestionInputType === QUESTION_INPUT_TYPE.NUMBER_RANGE ? selectedQuestionForDateAnalysis?.numberRangeType : null,
													timeRangeType: selectedQuestionForDateAnalysis?.customQuestionInputType === QUESTION_INPUT_TYPE.TIME_TAB_RANGE ? selectedQuestionForDateAnalysis?.timeRangeType : null,
													autoFillEnabled: selectedQuestionForDateAnalysis?.autoFillEnabled || false,
													sourceTransitionType: selectedQuestionForDateAnalysis?.sourceTransitionType || '',
													sourceQuestionId: selectedQuestionForDateAnalysis?.sourceQuestionId || '',
													dateAnalysisStartQuestion: selectedQuestionForDateAnalysis?.dateAnalysisStartQuestion || '',
													dateAnalysisEndQuestion: selectedQuestionForDateAnalysis?.dateAnalysisEndQuestion || '',
													useCustomCardInDashboard: selectedQuestionForDateAnalysis?.useCustomCardInDashboard || false,
													description: selectedQuestionForDateAnalysis?.description || '',
													aiDescription: selectedQuestionForDateAnalysis?.aiDescription || '',
													invisibleAnswerOptions: selectedQuestionForDateAnalysis?.invisibleAnswerOptions || false,
													dataInputLabel: selectedQuestionForDateAnalysis?.dataInputLabel || '',
													isArchived: selectedQuestionForDateAnalysis?.isArchived || false,
													isUniversal: selectedQuestionForDateAnalysis?.isUniversal || false,
												});
												setEditQuestion(selectedQuestionForDateAnalysis);
												setShowAddQ(true);
												setSelectedQuestionForDateAnalysis(null);
											}
										}}
									/>
								</div>

								{/* Related Questions Section */}
								{dateAnalysisQuestions.length > 0 && (() => {
									// Group questions into two groups
									const defaultCardQuestions = dateAnalysisQuestions.filter(
										(questionItem) =>
											questionItem.question?.isDefaultCard === true &&
											!questionItem.question?.existingAccessor
									);
									const otherQuestions = dateAnalysisQuestions.filter(
										(questionItem) =>
											!(questionItem.question?.isDefaultCard === true &&
												!questionItem.question?.existingAccessor)
									);

									const renderQuestionItem = (questionItem, index) => (
										<div
											key={index}
											style={{
												padding: '12px',
												border: '1px solid #e0e0e0',
												borderRadius: '4px',
												backgroundColor: '#fafafa',
												display: 'flex',
												justifyContent: 'space-between',
												alignItems: 'center'
											}}
										>
											<span style={{ fontSize: '13px', color: '#333' }}>
												{questionItem.question.order?.label || questionItem.question.label}
											</span>
											<Button
												text="Edit"
												buttonStyle="theme"
												size="medium"
												action={() => {
													setShowDateAnalysisOptionsDialog(false);
													setQuestionValues({
														label: questionItem.question.order?.label || questionItem.question.label,
														tableLabel: questionItem.question.order?.tableLabel || questionItem.question.tableLabel,
														customQuestionInputType: questionItem.question?.customQuestionInputType,
														customQuestionOptions: questionItem.question?.customQuestionOptions,
														templateType: questionItem.question?.templateType ?? QUESTIONS_TEMPLATE_TYPE.DX_CARD,
														numberRangeType: questionItem.question?.customQuestionInputType === QUESTION_INPUT_TYPE.NUMBER_RANGE ? questionItem.question?.numberRangeType : null,
														timeRangeType: questionItem.question?.customQuestionInputType === QUESTION_INPUT_TYPE.TIME_TAB_RANGE ? questionItem.question?.timeRangeType : null,
														autoFillEnabled: questionItem.question?.autoFillEnabled || false,
														sourceTransitionType: questionItem.question?.sourceTransitionType || '',
														sourceQuestionId: questionItem.question?.sourceQuestionId || '',
														dateAnalysisStartQuestion: questionItem.question?.dateAnalysisStartQuestion || '',
														dateAnalysisEndQuestion: questionItem.question?.dateAnalysisEndQuestion || '',
														useCustomCardInDashboard: questionItem.question?.useCustomCardInDashboard || false,
														description: questionItem.question?.description || '',
														aiDescription: questionItem.question?.aiDescription || '',
														existingAccessor: questionItem.question?.existingAccessor || '',
														invisibleAnswerOptions: questionItem.question?.invisibleAnswerOptions || false,
														dataInputLabel: questionItem.question?.dataInputLabel || ''
													});
													setEditQuestion(questionItem.question);
													setShowAddQ(true);
												}}
											/>
										</div>
									);

									return (
										<div>
											<h4 style={{ margin: '0 0 12px 0', color: '#333', fontSize: '14px', fontWeight: '600' }}>
												Related {questionTypeLabel} Analysis Questions :
											</h4>

											{/* Default Card Questions Group */}
											{defaultCardQuestions.length > 0 && (
												<div style={{ marginBottom: '20px' }}>
													<h5 style={{ margin: '0 0 12px 0', color: '#555', fontSize: '13px', fontWeight: '600' }}>
														Default Card Questions ({defaultCardQuestions.length})
													</h5>
													<div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
														{defaultCardQuestions.map((questionItem, index) => renderQuestionItem(questionItem, index))}
													</div>
												</div>
											)}

											{/* Other Questions Group */}
											{otherQuestions.length > 0 && (
												<div>
													<h5 style={{ margin: '0 0 12px 0', color: '#555', fontSize: '13px', fontWeight: '600' }}>
														Other Questions ({otherQuestions.length})
													</h5>
													<div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
														{otherQuestions.map((questionItem, index) => renderQuestionItem(questionItem, index))}
													</div>
												</div>
											)}
										</div>
									);
								})()}

								{dateAnalysisQuestions.length === 0 && (
									<div style={{
										padding: '16px',
										textAlign: 'center',
										color: '#666',
										fontSize: '13px'
									}}>
										No related {questionTypeLabel.toLowerCase()} questions found.
									</div>
								)}
							</>
						);
					})()}
				</DialogContent>
			</Dialog>

			{/* AI Configuration Dialog */}
			<ShareCustomQuestionDialog
				open={showShareDialog}
				onClose={handleShareDialogClose}
				customQuestion={selectedQuestionForShare}
				onShareSuccess={handleShareSuccess}
			/>

			<AiConfigDialog
				open={showAiConfigDialog}
				onClose={() => {
					setShowAiConfigDialog(false);
					setSelectedQuestionForAi(null);
				}}
				question={selectedQuestionForAi}
				onSave={(aiSettings) => {
					// Refresh questions after AI settings are saved
					getQuestions();
					setShowAiConfigDialog(false);
					setSelectedQuestionForAi(null);
				}}
				facilityId={facilityId}
			/>
		</>
	);
};

export default QuestionSetup;
